import {
  type ChangeAcknowledgement,
  acknowledgementOf,
  type ChangeConsequences,
  type FactId,
  type MaterialsFileRef,
  type MaterialsInputs,
  type MaterialsShape,
  type StudyFileMeta,
  type StudyTypeSelection,
  MATERIALS_EXTRACTION_POLL_MS,
  MATERIALS_EXTRACTION_WAIT_MS,
  MAX_FACT_VALUE_CHARS,
  MAX_MATERIALS_FILE_BYTES,
  MAX_MATERIALS_DESCRIPTION_CHARS,
  MAX_MATERIALS_DIFFERS_CHARS,
  MAX_MATERIALS_FIELD_CHARS,
  MAX_MATERIALS_LIST_ITEM_CHARS,
  MAX_MATERIALS_TEXT_CHARS,
  MAX_TEXT_INTAKE_FILES,
  canonicalJson,
  materialsFileRefs,
  materialsInputsProblems,
  materialsInputsSchema,
  mebibytes,
  samePairedVersionsProblem,
  stimulusCandidates,
  studyTypeName,
} from '@sigora/domain';
import { type FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  type MaterialsRowView,
  type MaterialsView,
  approveArtifact,
  correctMaterialsFact,
  type MaterialsHistory,
  fetchMaterials,
  fetchMaterialsHistory,
  materialsFileUrl,
  saveMaterials,
  UploadInterruptedError,
  setMaterialsStimulus,
  uploadMaterialsFile,
} from '../api/client.ts';
import { domainCodeOf, isApiError } from '../api/errors.ts';
import {
  type FileSlot,
  type StimulusControl,
  type Upload,
  MaterialsFiles,
} from './MaterialsFiles.tsx';
import { ChangeConfirm, unacknowledgedChangeOf } from './ChangeConsequences.tsx';
import { MATERIALS_HISTORY_TOGGLE, materialsHistoryLine } from './materials-copy.ts';
import { coverageLine } from './materials-files.ts';
import { Pending } from '../ui/Pending.tsx';
import {
  CONFIRMING_LABEL,
  MATERIALS_CHANGE_PENDING_HINT,
  MATERIALS_EDIT_OPEN_HINT,
  MATERIALS_MISSING_HINT,
  MATERIALS_NOTHING_SAVED_HINT,
  MATERIALS_NOT_CONFIRMABLE_HINT,
  MATERIALS_READING_HINT,
  MATERIALS_UNSAVED_HINT,
  MATERIALS_UPLOADING_HINT,
  SAVING_HINT,
} from './continue-copy.ts';
import { dateOf } from './dates.ts';
import { useRefusalFocus } from './refusal-focus.ts';
import { useContinueAction } from './step-footer.tsx';

export interface MaterialsStepProps {
  studyId: string;
  /** What the study read says the study's type is now, which the saved Materials are compared to. */
  studyType: StudyTypeSelection | null;
  /**
   * Whether the notice above the step already says why nothing on it can change. When the
   * Materials read says they cannot change and the study read the shell holds says nothing of a
   * hold — a checkout opened in another tab, or a study read from before it — the step says so
   * itself, so read-only controls never go unexplained.
   */
  frozen: boolean;
  /** Bumped by the shell whenever the study may have changed; the step is read again on it. */
  refreshToken: number;
  /** Tells the shell something here changed, so the steps and the sheet are read again. */
  onStudyChanged: () => void;
}

/**
 * What the reader is typing, per intake. Strings throughout — a list field is one box per entry —
 * so nothing is trimmed or dropped until the moment it is sent. The files the saved inputs name
 * ride along unchanged, so a save that edits the words keeps them.
 */
type Draft =
  | { kind: 'text'; text: string; files: readonly MaterialsFileRef[] }
  | { kind: 'form'; fields: Record<string, string | string[]> }
  | {
      kind: 'paired';
      descriptions: [string, string];
      files: [MaterialsFileRef | null, MaterialsFileRef | null];
      differs: string;
    };

/** A draft for a shape, filled from saved inputs where they were taken through the same intake. */
const draftFor = (shape: MaterialsShape, inputs: MaterialsInputs | null): Draft => {
  const { intake } = shape;

  if (intake.kind === 'text') {
    return inputs?.kind === 'text'
      ? { kind: 'text', text: inputs.text ?? '', files: inputs.files }
      : { kind: 'text', text: '', files: [] };
  }

  if (intake.kind === 'form') {
    const saved = inputs?.kind === 'form' ? inputs.fields : {};

    return {
      kind: 'form',
      fields: Object.fromEntries(
        intake.fields.map((field) => {
          const value = Object.hasOwn(saved, field.id) ? saved[field.id] : undefined;
          if (field.maxItems === undefined) {
            return [field.id, typeof value === 'string' ? value : ''];
          }

          const entries = Array.isArray(value) ? value : [];

          return [
            field.id,
            Array.from({ length: field.maxItems }, (_, index) => entries[index] ?? ''),
          ];
        }),
      ),
    };
  }

  return inputs?.kind === 'paired'
    ? {
        kind: 'paired',
        descriptions: [inputs.versions[0].description ?? '', inputs.versions[1].description ?? ''],
        files: [inputs.versions[0].file, inputs.versions[1].file],
        differs: inputs.differs ?? '',
      }
    : { kind: 'paired', descriptions: ['', ''], files: [null, null], differs: '' };
};

type Built = { ok: true; inputs: MaterialsInputs } | { ok: false; problem: string };

/**
 * The inputs a draft would send, trimmed and emptied the way the server trims them, and checked
 * against the same schema and shape rule the server applies — so what is refused here is what the
 * server would refuse, in words the reader can act on.
 */
const buildInputs = (shape: MaterialsShape, draft: Draft): Built => {
  const { intake } = shape;
  let candidate: unknown;

  if (draft.kind === 'text') {
    const text = draft.text.trim();
    if (text === '' && draft.files.length === 0) {
      return {
        ok: false,
        problem: 'Describe your study, paste your brief or add a file before saving.',
      };
    }

    candidate = { kind: 'text', text: text === '' ? null : text, files: draft.files };
  } else if (draft.kind === 'form') {
    const fields = Object.entries(draft.fields).flatMap(([id, value]): [string, unknown][] => {
      if (Array.isArray(value)) {
        const entries = value.map((entry) => entry.trim()).filter((entry) => entry !== '');

        return entries.length === 0 ? [] : [[id, entries]];
      }

      const trimmed = value.trim();

      return trimmed === '' ? [] : [[id, trimmed]];
    });
    if (fields.length === 0) {
      return { ok: false, problem: 'Fill in at least one field before saving.' };
    }

    candidate = { kind: 'form', fields: Object.fromEntries(fields) };
  } else {
    if (intake.kind !== 'paired') {
      return { ok: false, problem: 'These materials do not fit what this study takes.' };
    }

    const descriptions = draft.descriptions.map((description) => description.trim());
    if (
      descriptions.some((description, index) => description === '' && draft.files[index] === null)
    ) {
      return { ok: false, problem: 'Describe each version, or add a file for it, before saving.' };
    }

    const differs = draft.differs.trim();
    candidate = {
      kind: 'paired',
      versions: intake.labels.map((label, index) => ({
        label,
        description: descriptions[index] === '' ? null : descriptions[index],
        file: draft.files[index] ?? null,
      })),
      differs: differs === '' ? null : differs,
    };
  }

  const parsed = materialsInputsSchema.safeParse(candidate);
  if (!parsed.success) {
    return {
      ok: false,
      problem: `These materials do not fit what this study takes: ${parsed.error.issues
        .map((issue) => issue.message)
        .join('; ')}.`,
    };
  }

  const problems = materialsInputsProblems(shape, parsed.data);
  // Two paired versions that are the same are something the reader typed, not a misfit, so the
  // rule's own sentence is said on its own, under the slots, before anything is sent.
  const same =
    intake.kind === 'paired'
      ? problems.find((problem) => problem === samePairedVersionsProblem(intake.labels))
      : undefined;
  if (same !== undefined) {
    return { ok: false, problem: `${same}.` };
  }

  return problems.length === 0
    ? { ok: true, inputs: parsed.data }
    : {
        ok: false,
        problem: `These materials do not fit what this study takes: ${problems.join('; ')}.`,
      };
};

/**
 * The intake as the boxes show it, and whether it is what is saved. They show the reader's draft
 * once they have typed into this intake, and otherwise what is saved. A draft typed into another
 * kind of intake — the study's type has since changed — has nothing to say about this one, so it
 * starts again from what is saved. What is saved counts as unchanged only when it was captured for
 * the study's type as it is now.
 */
const intakeAsShown = (
  shape: MaterialsShape,
  draft: Draft | null,
  saved: MaterialsInputs | null,
  capturedHere: boolean,
): { shown: Draft; built: Built; unchanged: boolean } => {
  const shown = draft !== null && draft.kind === shape.intake.kind ? draft : draftFor(shape, saved);
  const built = buildInputs(shape, shown);

  return {
    shown,
    built,
    unchanged:
      capturedHere &&
      saved !== null &&
      built.ok &&
      canonicalJson(built.inputs) === canonicalJson(saved),
  };
};

const sameStudyType = (
  captured: { id: string; template_version: number },
  selection: StudyTypeSelection | null,
): boolean =>
  selection !== null &&
  captured.id === selection.id &&
  captured.template_version === selection.templateVersion;

const failureMessage = (failure: unknown, fallback: string): string =>
  failure instanceof Error ? failure.message : fallback;

/** What a row says in place of a value, and the note beside it. */
const rowValue = (row: MaterialsRowView): string => {
  if (row.value !== null) {
    return row.value;
  }

  if (row.state === 'not_read') {
    return 'not read yet';
  }

  // Part of a file went unread, so all that is known is that the parts read did not say.
  return row.state === 'none_found_in_read' ? 'Not found in what Sigora could read' : 'none found';
};

/** Names as a list a sentence can hold: "a.pdf, b.md and c.png". */
const nameList = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;

/**
 * Whether sending the same file again could get a different answer: after a dropped connection or
 * a server fault, and after a file that arrived damaged, but not after a refusal of the file itself
 * or of the study, which only a different file or a different study state changes, and not after
 * an answer this page could not read.
 */
const retryableUpload = (failure: unknown): boolean =>
  failure instanceof UploadInterruptedError ||
  (isApiError(failure) && (failure.status >= 500 || failure.code === 'file_damaged'));

const isAbort = (failure: unknown): boolean =>
  failure instanceof DOMException && failure.name === 'AbortError';

/**
 * The Materials step: add what you have, and check what Sigora took from it.
 *
 * The intake is whatever the study's type asks for — a description or a pasted brief, a form, or
 * two versions and what differs between them — and Save stores it as a new version for the worker
 * to read. The rows are the server's, in the order the type lists its facts; each is fixed on its
 * own, so a fix never carries its neighbours with it. The footer's Continue confirms the version
 * on screen and nothing newer, which the server refuses if the Materials moved; it waits while
 * anything typed is unsaved, so it never confirms facts the reader has not seen.
 *
 * Save is the only way the inputs are sent, and it is offered only while they differ from what is
 * saved: an unchanged save is either nothing or a new reading, and a new reading of a study whose
 * rows were filled in by hand is asked for with its own button rather than by leaving the step.
 */
export const MaterialsStep = ({
  studyId,
  studyType,
  frozen,
  refreshToken,
  onStudyChanged,
}: MaterialsStepProps) => {
  const [view, setView] = useState<MaterialsView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Why the screen was read again under the reader, said once over the step. */
  const [notice, setNotice] = useState<string | null>(null);
  /** The one row being fixed, and what is typed into it. */
  const [editing, setEditing] = useState<{ factId: FactId; value: string } | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  /** The row whose Edit button takes the focus back once its editor closes. */
  const [returnFocusTo, setReturnFocusTo] = useState<FactId | null>(null);
  /** Files on their way to the study, and the ones that did not get there. */
  const [uploads, setUploads] = useState<readonly Upload[]>([]);
  /** What each upload answered, so a file the draft names before it is saved can be shown. */
  const [uploaded, setUploaded] = useState<ReadonlyMap<string, StudyFileMeta>>(new Map());
  /** Said once over one intake's files, keyed by the slot. */
  const [fileNotices, setFileNotices] = useState<Readonly<Record<string, string>>>({});
  const [choosing, setChoosing] = useState(false);
  /**
   * A change the server will make only once the reader has seen what it does below the Materials —
   * a save, a fixed row or an image marked or unmarked — with what it said that is. What was typed
   * stays as it is under it, whichever way they answer.
   */
  const [pendingChange, setPendingChange] = useState<
    | ((
        | { action: 'save'; inputs: MaterialsInputs }
        | { action: 'fix' }
        | { action: 'stimulus'; fileId: string; candidate: boolean }
      ) & { consequences: ChangeConsequences })
    | null
  >(null);
  // A save waiting on the reader's say-so was for the inputs as they were sent, not as typed since.
  useEffect(() => {
    setPendingChange((pending) => (pending?.action === 'save' ? null : pending));
  }, [draft]);
  const [stimulusError, setStimulusError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** The history as last read, while it is open; read again whenever the Materials move. */
  const [history, setHistory] = useState<MaterialsHistory | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  /** Each upload on its way, by key: what stops it, and the intake it is for. */
  const uploadControllers = useRef(
    new Map<string, { controller: AbortController; slot: FileSlot }>(),
  );
  /**
   * The files added to the text draft since the last commit. Two uploads of the same bytes can both
   * answer before the draft they were added to renders, and neither would see the other's file in
   * `latestShown`; this is what the second is checked against.
   */
  const attachedSinceCommit = useRef(new Set<string>());
  const uploadCount = useRef(0);
  /**
   * What an upload that answers later adds its file to: the shape and saved inputs of the render
   * it lands in, and the draft that render shows. An answer arrives after the render that sent it,
   * so reading them from that render's closure would add the file to a draft since replaced.
   */
  const latestShown = useRef<{
    shape: MaterialsShape | null;
    inputs: MaterialsInputs | null;
    draft: Draft | null;
  }>({ shape: null, inputs: null, draft: null });

  /** The request that stopped being waited on, so a stale deadline cannot silence a new one. */
  const [waitedOut, setWaitedOut] = useState<string | null>(null);
  const pendingSince = useRef<{ requestId: string; at: number } | null>(null);
  /** Counts the poll's ticks, so each tick arms the next whatever the read it asked for returned. */
  const [tick, setTick] = useState(0);

  /**
   * Only the newest answer is shown. A poll's read and a save's response can cross, and the older of
   * the two landing last would put back rows the reader has just replaced.
   */
  const latest = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    const controllers = uploadControllers.current;

    return () => {
      alive.current = false;
      for (const { controller } of controllers.values()) {
        controller.abort();
      }
    };
  }, []);

  const fixButtons = useRef(new Map<FactId, HTMLButtonElement>());
  const intakeRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLInputElement | null>(null);

  const issue = () => {
    latest.current += 1;

    return latest.current;
  };

  const show = useCallback((ticket: number, next: MaterialsView) => {
    if (alive.current && ticket === latest.current) {
      setView(next);
      setLoadError(null);
    }
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      latest.current += 1;
      const ticket = latest.current;

      try {
        show(ticket, await fetchMaterials(studyId, signal));
      } catch (failure: unknown) {
        // A read given up on is not a read that failed: the deadline that aborted it says so itself.
        if (alive.current && ticket === latest.current && signal?.aborted !== true) {
          setLoadError(failureMessage(failure, 'Your materials could not be read.'));
        }
      }
    },
    [studyId, show],
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const shape = view?.shape ?? null;
  const current = view?.current ?? null;
  const capturedHere = current !== null && sameStudyType(current.content.study_type, studyType);

  // A layout effect, so the refs match the committed screen before any event can reach it: a
  // passive effect runs after paint, and a pick in that gap would count against an older draft.
  useLayoutEffect(() => {
    latestShown.current = { shape, inputs: current?.content.inputs ?? null, draft };
    attachedSinceCommit.current.clear();
  });

  /** The draft the latest render shows, or null while there is no intake to show one for. */
  const heldDraft = (): Draft | null => {
    const { shape: now, inputs, draft: typed } = latestShown.current;
    if (now === null) {
      return null;
    }

    return typed !== null && typed.kind === now.intake.kind ? typed : draftFor(now, inputs);
  };

  /**
   * Changes the draft as the latest render shows it, which is what a file that finished uploading
   * is added to — the draft typed since it was sent included.
   */
  const updateDraft = useCallback((change: (held: Draft) => Draft) => {
    setDraft((previous) => {
      const { shape: now, inputs } = latestShown.current;
      if (now === null) {
        return previous;
      }

      return change(
        previous !== null && previous.kind === now.intake.kind ? previous : draftFor(now, inputs),
      );
    });
  }, []);

  const extraction = current?.content.extraction ?? null;
  const pendingId = capturedHere && extraction?.status === 'pending' ? extraction.request_id : null;
  const waiting = pendingId !== null && waitedOut !== pendingId;

  /** The poll's read in flight, which the deadline abandons rather than waits on. */
  const pollRead = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      pollRead.current?.abort();
    },
    [],
  );

  const startWaiting = (requestId: string): number => {
    if (pendingSince.current?.requestId !== requestId) {
      pendingSince.current = { requestId, at: Date.now() };
    }

    return pendingSince.current.at;
  };

  /**
   * The deadline, on a timer of its own, armed once for each request waited on. The poll only asks
   * again once its read has answered, so a read that never answers would otherwise hold the step
   * pending for good; this gives up on it at the deadline, aborts it, and offers the retry.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    const since = startWaiting(pendingId);
    const deadline = setTimeout(
      () => {
        pollRead.current?.abort();
        setWaitedOut(pendingId);
      },
      Math.max(0, since + MATERIALS_EXTRACTION_WAIT_MS - Date.now()),
    );

    return () => clearTimeout(deadline);
  }, [pendingId, waitedOut]);

  /**
   * One timer at a time, and none once the reading has settled or the wait has run out. Each tick
   * arms the next through its own counter, so a run of failed reads still reaches the deadline,
   * and only once its read has answered: at most one read is in flight, and a read slower than the
   * interval is not overtaken by the next one and thrown away as stale. Torn down with the render
   * that armed it, so nothing is left ticking when the step moves on.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    const since = startWaiting(pendingId);
    const overdue = () => Date.now() - since >= MATERIALS_EXTRACTION_WAIT_MS;
    if (overdue()) {
      setWaitedOut(pendingId);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (overdue()) {
        setWaitedOut(pendingId);
        return;
      }

      const controller = new AbortController();
      pollRead.current = controller;
      void load(controller.signal).then(() => {
        if (alive.current && !controller.signal.aborted) {
          setTick((count) => count + 1);
        }
      });
    }, MATERIALS_EXTRACTION_POLL_MS);

    return () => clearTimeout(timer);
  }, [pendingId, waitedOut, load, tick]);

  /**
   * A reading that settles can retitle the study, and it changes what the sheet says, so the shell
   * is told once when one stops being pending.
   */
  const wasPending = useRef<string | null>(null);
  useEffect(() => {
    if (wasPending.current !== null && pendingId === null) {
      onStudyChanged();
    }

    wasPending.current = pendingId;
  }, [pendingId, onStudyChanged]);

  const editingId = editing?.factId ?? null;
  useEffect(() => {
    if (editingId !== null) {
      editorRef.current?.focus();
    }
  }, [editingId]);

  useEffect(() => {
    if (returnFocusTo !== null && editing === null) {
      fixButtons.current.get(returnFocusTo)?.focus();
      setReturnFocusTo(null);
    }
  }, [returnFocusTo, editing]);

  /**
   * The history is read on its own, and only while it is open: it parses every version, and the
   * step's own read is polled while a reading runs. Keyed on what a new version or a confirmation
   * changes, so an open history follows the Materials as they move.
   */
  const historyKey =
    view?.current == null
      ? null
      : `${String(view.current.version)}:${String(view.current.approvedVersion)}:${String(view.confirmedCurrent)}`;
  useEffect(() => {
    if (!historyOpen || historyKey === null) {
      return undefined;
    }

    const controller = new AbortController();
    void fetchMaterialsHistory(studyId, controller.signal).then(
      (read) => {
        setHistory(read);
        setHistoryError(null);
      },
      (failure: unknown) => {
        if (!controller.signal.aborted) {
          setHistoryError(
            failure instanceof Error ? failure.message : 'The history could not be read.',
          );
        }
      },
    );

    return () => controller.abort();
  }, [historyOpen, historyKey, studyId]);

  const send = (
    inputs: MaterialsInputs,
    acknowledgedDownstream: ChangeAcknowledgement | null = null,
  ) => {
    if (view === null) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    setNotice(null);
    setConfirmError(null);
    setPendingChange(null);
    const ticket = issue();

    void saveMaterials({
      studyId,
      inputs,
      expectedVersion: current?.version ?? 0,
      acknowledgedDownstream,
    }).then(
      (saved) => {
        setSaving(false);
        show(ticket, saved);
        // What is saved is what the boxes show from here, until the reader types again.
        setDraft(null);
        onStudyChanged();
      },
      (failure: unknown) => {
        setSaving(false);
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setPendingChange({ action: 'save', inputs, consequences });
          return;
        }

        if (domainCodeOf(failure) === 'artifact_version_not_current') {
          setNotice('Your materials changed while you were editing. Check them and save again.');
          void load();
          return;
        }

        setSaveError(failureMessage(failure, 'Your materials could not be saved.'));
      },
    );
  };

  const openEditor = (row: MaterialsRowView) => {
    setFixError(null);
    setEditing({ factId: row.factId, value: row.value ?? '' });
  };

  const closeEditor = () => {
    if (editing !== null) {
      setReturnFocusTo(editing.factId);
    }

    setEditing(null);
    setFixError(null);
    setPendingChange((pending) => (pending?.action === 'fix' ? null : pending));
  };

  const fix = (acknowledgedDownstream: ChangeAcknowledgement | null) => {
    if (editing === null || current === null) {
      return;
    }

    const value = editing.value.trim();
    setFixing(true);
    setFixError(null);
    setNotice(null);
    setPendingChange(null);
    const ticket = issue();

    void correctMaterialsFact({
      studyId,
      factId: editing.factId,
      value: value === '' ? null : value,
      baseVersion: current.version,
      acknowledgedDownstream,
    }).then(
      (fixed) => {
        setFixing(false);
        show(ticket, fixed);
        closeEditor();
        onStudyChanged();
      },
      (failure: unknown) => {
        setFixing(false);
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setPendingChange({ action: 'fix', consequences });
          return;
        }

        setFixError(failureMessage(failure, 'That row could not be saved.'));
      },
    );
  };

  const submitFix = (event: FormEvent) => {
    event.preventDefault();
    fix(null);
  };

  /** Confirms the version on screen; true once the server accepted it. */
  const confirm = (): Promise<boolean> => {
    if (current === null) {
      return Promise.resolve(false);
    }

    setConfirming(true);
    setConfirmError(null);
    setNotice(null);

    return approveArtifact({
      studyId,
      artifactId: current.artifactId,
      version: current.version,
    }).then(
      () => {
        setConfirming(false);
        void load();
        onStudyChanged();
        return true;
      },
      (failure: unknown) => {
        setConfirming(false);
        const code = domainCodeOf(failure);
        if (code === 'artifact_version_not_current') {
          setNotice('Your materials changed; check them again.');
        } else {
          setConfirmError(failureMessage(failure, 'Your materials could not be confirmed.'));
        }

        if (code !== null) {
          void load();
        }
        return false;
      },
    );
  };

  const busy = saving || fixing || confirming || choosing;

  const uploading = uploads.some((upload) => upload.status === 'uploading');
  /** The intake as the boxes show it; null while the step has no intake to show. */
  const intake =
    shape === null
      ? null
      : intakeAsShown(shape, draft, current?.content.inputs ?? null, capturedHere);

  /**
   * Why Continue cannot confirm the Materials yet, first what the reader can act on, or null when
   * it can. It never saves on the reader's behalf: a save can be asked about first, and makes a
   * version nobody has looked at.
   */
  const continueBlockedHint = (): string | null => {
    if (view === null) {
      return null;
    }

    if (editing !== null) {
      return MATERIALS_EDIT_OPEN_HINT;
    }

    if (pendingChange !== null) {
      return MATERIALS_CHANGE_PENDING_HINT;
    }

    if (busy) {
      return SAVING_HINT;
    }

    if (uploading) {
      return MATERIALS_UPLOADING_HINT;
    }

    if (!capturedHere) {
      return MATERIALS_NOTHING_SAVED_HINT;
    }

    if (intake?.unchanged !== true) {
      return MATERIALS_UNSAVED_HINT;
    }

    if (extraction?.status === 'pending') {
      return MATERIALS_READING_HINT;
    }

    if (view.missing.length > 0) {
      return MATERIALS_MISSING_HINT;
    }

    // The server's own answer to whether this version would be accepted, for anything the causes
    // above do not name.
    return view.confirmable ? null : MATERIALS_NOT_CONFIRMABLE_HINT;
  };
  const continueHint = continueBlockedHint();

  const { refusal, continueRun } = useRefusalFocus();
  /**
   * Continue confirms Materials the study may still change and that are not confirmed as they
   * stand, and stays on the step over a refusal, which it takes the reader to. Confirmed or fixed
   * Materials leave Continue a plain move.
   */
  useContinueAction(
    view === null || shape === null || !view.editable || view.confirmedCurrent
      ? null
      : {
          run: continueRun(confirm),
          disabled: continueHint !== null,
          blockedHint: continueHint,
          busyLabel: CONFIRMING_LABEL,
        },
  );

  const focusIntake = () => {
    intakeRef.current?.querySelector<HTMLElement>('textarea, input')?.focus();
  };

  if (view === null) {
    return loadError === null ? (
      <p data-test-id="materials-loading">Reading your materials…</p>
    ) : (
      <p className="chat-error" data-test-id="materials-error" role="alert">
        {loadError}{' '}
        <button type="button" data-test-id="materials-reload" onClick={() => void load()}>
          Try again
        </button>
      </p>
    );
  }

  if (shape === null || intake === null) {
    return (
      <div className="materials" data-test-id="materials">
        <p className="materials-lede" data-test-id="materials-untyped">
          Pick a study type first. It decides what Sigora asks for here.
        </p>
      </div>
    );
  }

  const { shown, built, unchanged } = intake;
  /**
   * Whether anything here may change, as the server decides it: a draft or ready study nobody has
   * locked, launched or paid for, and no purchase holds. A change on a ready study takes it back to
   * draft until the Materials are confirmed again. Past that the step is shown as it stands — the
   * inputs read-only and every control that writes disabled — rather than offering edits the server
   * will refuse.
   */
  const readOnly = !view.editable;
  /**
   * Whether to offer a new reading of what is saved: only while a reading could not be made, and
   * never over a confirmation, which a new reading would take away.
   */
  const settledUnread =
    capturedHere &&
    unchanged &&
    !view.confirmedCurrent &&
    (extraction?.status === 'unavailable' ||
      extraction?.status === 'unreadable' ||
      (extraction?.status === 'pending' && !waiting));
  const typeName = studyType === null ? undefined : studyTypeName(studyType.id);

  const setField = (id: string, value: string | string[]) => {
    if (shown.kind === 'form') {
      setDraft({ ...shown, fields: { ...shown.fields, [id]: value } });
    }
  };

  /** Every file the screen can name: the saved version's, then any uploaded since. */
  const known = new Map<string, StudyFileMeta>(uploaded);
  for (const file of view.files) {
    known.set(file.id, file);
  }

  const noteFiles = (slot: FileSlot, note: string | null) => {
    setFileNotices((notes) => {
      const next = { ...notes };
      if (note === null) {
        delete next[String(slot)];
      } else {
        next[String(slot)] = note;
      }

      return next;
    });
  };

  const attach = (slot: FileSlot, picked: File, stored: StudyFileMeta) => {
    setUploaded((files) => new Map(files).set(stored.id, stored));
    const ref = { file_id: stored.id, kind: stored.kind };
    const shownNow = heldDraft();

    if (slot === 'text') {
      if (
        attachedSinceCommit.current.has(stored.id) ||
        (shownNow?.kind === 'text' && shownNow.files.some((file) => file.file_id === stored.id))
      ) {
        // The study keeps one copy of any bytes, so the same file again is the file already here.
        noteFiles(
          slot,
          stored.name === picked.name
            ? `${stored.name} is already added.`
            : `${picked.name} is already added, as ${stored.name}.`,
        );
        return;
      }

      attachedSinceCommit.current.add(stored.id);
      noteFiles(slot, null);
      updateDraft((held) =>
        held.kind === 'text' ? { ...held, files: [...held.files, ref] } : held,
      );
      return;
    }

    noteFiles(slot, null);
    updateDraft((held) =>
      held.kind === 'paired'
        ? { ...held, files: slot === 0 ? [ref, held.files[1]] : [held.files[0], ref] }
        : held,
    );
  };

  /**
   * How many more files the text intake takes: the limit, less the files the draft names — those
   * attached since the last render included — and the uploads on their way. Read from refs rather
   * than from this render, so two picks landing before the screen draws again share one count.
   */
  const textRoomLeft = (): number => {
    const held = heldDraft();
    const named = new Set(held?.kind === 'text' ? held.files.map((file) => file.file_id) : []);
    for (const fileId of attachedSinceCommit.current) {
      named.add(fileId);
    }

    const sending = [...uploadControllers.current.values()].filter(
      (upload) => upload.slot === 'text',
    ).length;

    return MAX_TEXT_INTAKE_FILES - named.size - sending;
  };

  const startUpload = (key: string, slot: FileSlot, file: File) => {
    const controller = new AbortController();
    uploadControllers.current.set(key, { controller, slot });
    const progress = (sent: number, total: number) =>
      setUploads((list) =>
        list.map((upload) => (upload.key === key ? { ...upload, sent, total } : upload)),
      );

    void uploadMaterialsFile(studyId, file, progress, controller.signal).then(
      ({ file: stored }) => {
        uploadControllers.current.delete(key);
        if (!alive.current) {
          return;
        }

        setUploads((list) => list.filter((upload) => upload.key !== key));
        attach(slot, file, stored);
      },
      (failure: unknown) => {
        uploadControllers.current.delete(key);
        if (!alive.current || isAbort(failure)) {
          return;
        }

        setUploads((list) =>
          list.map((upload) =>
            upload.key === key
              ? {
                  ...upload,
                  status: 'failed',
                  error: failureMessage(failure, 'The file could not be uploaded.'),
                  retryable: retryableUpload(failure),
                }
              : upload,
          ),
        );
      },
    );
  };

  /**
   * Sends each picked file, after the checks that need no server: a file over the size limit and
   * one past the intake's count are refused here, in the words the server would use. A paired
   * version takes one file, so a second picked for it replaces the first.
   */
  const pick = (slot: FileSlot, files: readonly File[]) => {
    if (files.length === 0 || readOnly) {
      return;
    }

    let room: number;
    let taken: readonly File[];
    if (slot === 'text') {
      room = textRoomLeft();
      taken = files;
      noteFiles(slot, null);
    } else {
      // A version names one file, so whatever was on its way for it is replaced.
      for (const [key, upload] of uploadControllers.current) {
        if (upload.slot === slot) {
          upload.controller.abort();
          uploadControllers.current.delete(key);
        }
      }

      setUploads((list) => list.filter((upload) => upload.slot !== slot));
      room = 1;
      taken = files.slice(0, 1);
      noteFiles(
        slot,
        files.length > 1
          ? `A version takes one file, so Sigora added ${files[0]?.name ?? 'the first'} only.`
          : null,
      );
    }

    const next = taken.map((file): Upload => {
      uploadCount.current += 1;
      const base = {
        key: `upload-${String(uploadCount.current)}`,
        slot,
        file,
        sent: 0,
        total: file.size,
      };

      if (file.size > MAX_MATERIALS_FILE_BYTES) {
        return {
          ...base,
          status: 'failed',
          error: `Files can be up to ${mebibytes(MAX_MATERIALS_FILE_BYTES)}.`,
          retryable: false,
        };
      }

      if (room <= 0) {
        return {
          ...base,
          status: 'failed',
          error: `Add at most ${String(MAX_TEXT_INTAKE_FILES)} files.`,
          retryable: false,
        };
      }

      room -= 1;

      return { ...base, status: 'uploading', error: null, retryable: false };
    });

    setUploads((list) => [...list, ...next]);
    for (const upload of next) {
      if (upload.status === 'uploading') {
        startUpload(upload.key, slot, upload.file);
      }
    }
  };

  const retryUpload = (key: string) => {
    const upload = uploads.find((candidate) => candidate.key === key);
    if (upload === undefined) {
      return;
    }

    // A retry is another file for the intake, so it has to fit beside what was added meanwhile.
    if (upload.slot === 'text') {
      if (textRoomLeft() <= 0) {
        setUploads((list) =>
          list.map((candidate) =>
            candidate.key === key
              ? {
                  ...candidate,
                  error: `Add at most ${String(MAX_TEXT_INTAKE_FILES)} files.`,
                  retryable: false,
                }
              : candidate,
          ),
        );
        return;
      }
    }

    setUploads((list) =>
      list.map((candidate) =>
        candidate.key === key
          ? { ...candidate, status: 'uploading', sent: 0, error: null, retryable: false }
          : candidate,
      ),
    );
    startUpload(key, upload.slot, upload.file);
  };

  const dismissUpload = (key: string) => {
    uploadControllers.current.get(key)?.controller.abort();
    // Dropped now rather than when the abort lands, so a pick straight after counts its room back.
    uploadControllers.current.delete(key);
    setUploads((list) => list.filter((upload) => upload.key !== key));
  };

  const removeFile = (slot: FileSlot, fileId: string) => {
    noteFiles(slot, null);
    updateDraft((held) => {
      if (held.kind === 'text') {
        return { ...held, files: held.files.filter((file) => file.file_id !== fileId) };
      }

      if (held.kind === 'paired') {
        return { ...held, files: slot === 0 ? [null, held.files[1]] : [held.files[0], null] };
      }

      return held;
    });
  };

  /**
   * Says whether participants may see one saved image. The answer is a new version that keeps the
   * reading, so the rows stay and only the confirmation is asked for again.
   */
  const choose = (
    fileId: string,
    candidate: boolean,
    acknowledgedDownstream: ChangeAcknowledgement | null = null,
  ) => {
    if (current === null) {
      return;
    }

    setChoosing(true);
    setStimulusError(null);
    setNotice(null);
    setPendingChange(null);
    const ticket = issue();

    void setMaterialsStimulus({
      studyId,
      fileId,
      candidate,
      expectedVersion: current.version,
      acknowledgedDownstream,
    }).then(
      (next) => {
        setChoosing(false);
        show(ticket, next);
        onStudyChanged();
      },
      (failure: unknown) => {
        setChoosing(false);
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setPendingChange({ action: 'stimulus', fileId, candidate, consequences });
          return;
        }

        if (domainCodeOf(failure) === 'artifact_version_not_current') {
          setNotice('Your materials changed while you were choosing. Check them and choose again.');
          void load();
          return;
        }

        setStimulusError(failureMessage(failure, 'That choice could not be saved.'));
      },
    );
  };

  const savedRefs = capturedHere ? materialsFileRefs(current.content.inputs) : [];
  const stimulus: StimulusControl = {
    saved: new Set(savedRefs.filter((ref) => ref.kind === 'image').map((ref) => ref.file_id)),
    chosen: new Set(capturedHere ? stimulusCandidates(current.content) : []),
    disabled: busy || pendingChange !== null,
    onChange: (fileId, candidate) => choose(fileId, candidate),
  };

  const filesFor = (slot: FileSlot, refs: readonly MaterialsFileRef[]) => (
    <MaterialsFiles
      studyId={studyId}
      slot={slot}
      inputId={slot === 'text' ? 'materials-files' : `materials-file-${String(slot)}`}
      label={
        slot === 'text'
          ? 'Add files'
          : `Add a file for ${shape.intake.kind === 'paired' ? shape.intake.labels[slot] : 'this version'}`
      }
      multiple={slot === 'text'}
      refs={refs}
      known={known}
      uploads={uploads.filter((upload) => upload.slot === slot)}
      notice={fileNotices[String(slot)] ?? null}
      readOnly={readOnly}
      stimulus={stimulus}
      onPick={(files) => pick(slot, files)}
      onRemove={(fileId) => removeFile(slot, fileId)}
      onRetry={retryUpload}
      onDismiss={dismissUpload}
    />
  );

  const fileName = (fileId: string): string => known.get(fileId)?.name ?? 'A file';
  const readingNames = view.files.map((file) => file.name);

  return (
    <div className="materials" data-test-id="materials">
      {readOnly && !frozen && (
        <p className="materials-notice" data-test-id="materials-read-only" role="note">
          Materials can change only until the study is being paid for, paid for, frozen or launched.
        </p>
      )}

      {notice !== null && (
        <p
          ref={refusal}
          className="materials-notice"
          data-test-id="materials-notice"
          role="status"
          tabIndex={-1}
        >
          {notice}
        </p>
      )}

      <form
        className="materials-intake"
        data-test-id={`materials-intake-${shape.intake.kind}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (built.ok && !unchanged) {
            send(built.inputs);
          }
        }}
      >
        <div ref={intakeRef}>
          {shape.intake.kind === 'text' && shown.kind === 'text' && (
            <>
              <label htmlFor="materials-text">
                Describe your study in a sentence, or paste your brief.
              </label>
              <textarea
                id="materials-text"
                data-test-id="materials-text"
                readOnly={readOnly}
                rows={6}
                maxLength={MAX_MATERIALS_TEXT_CHARS}
                value={shown.text}
                onChange={(event) => setDraft({ ...shown, text: event.target.value })}
              />
              {filesFor('text', shown.files)}
            </>
          )}

          {shape.intake.kind === 'form' &&
            shown.kind === 'form' &&
            shape.intake.fields.map((field) => {
              const value = shown.fields[field.id];

              if (field.maxItems === undefined || !Array.isArray(value)) {
                return (
                  <div className="materials-field" key={field.id}>
                    <label htmlFor={`materials-field-${field.id}`}>{field.label}</label>
                    <input
                      id={`materials-field-${field.id}`}
                      data-test-id={`materials-field-${field.id}`}
                      readOnly={readOnly}
                      maxLength={MAX_MATERIALS_FIELD_CHARS}
                      value={typeof value === 'string' ? value : ''}
                      onChange={(event) => setField(field.id, event.target.value)}
                    />
                  </div>
                );
              }

              return (
                <fieldset className="materials-field materials-list" key={field.id}>
                  <legend>{field.label}</legend>
                  {value.map((entry, index) => (
                    // Positional by design: an entry is the box it was typed into.
                    // oxlint-disable-next-line react/no-array-index-key
                    <div key={index}>
                      <label
                        className="visually-hidden"
                        htmlFor={`materials-field-${field.id}-${String(index)}`}
                      >
                        {`${field.label}, ${String(index + 1)}`}
                      </label>
                      <input
                        id={`materials-field-${field.id}-${String(index)}`}
                        data-test-id={`materials-field-${field.id}-${String(index)}`}
                        readOnly={readOnly}
                        maxLength={MAX_MATERIALS_LIST_ITEM_CHARS}
                        value={entry}
                        onChange={(event) =>
                          setField(
                            field.id,
                            value.map((held, at) => (at === index ? event.target.value : held)),
                          )
                        }
                      />
                    </div>
                  ))}
                </fieldset>
              );
            })}

          {shape.intake.kind === 'paired' && shown.kind === 'paired' && (
            <>
              {shape.intake.labels.map((label, index) => (
                <div className="materials-field" key={label}>
                  <label htmlFor={`materials-version-${String(index)}`}>{label}</label>
                  <p className="materials-hint" id={`materials-version-hint-${String(index)}`}>
                    Describe it, add a file of it, or both.
                  </p>
                  <textarea
                    id={`materials-version-${String(index)}`}
                    data-test-id={`materials-version-${String(index)}`}
                    aria-describedby={`materials-version-hint-${String(index)}`}
                    readOnly={readOnly}
                    rows={4}
                    maxLength={MAX_MATERIALS_DESCRIPTION_CHARS}
                    value={shown.descriptions[index]}
                    onChange={(event) =>
                      setDraft({
                        ...shown,
                        descriptions:
                          index === 0
                            ? [event.target.value, shown.descriptions[1]]
                            : [shown.descriptions[0], event.target.value],
                      })
                    }
                  />
                  {(index === 0 || index === 1) &&
                    filesFor(index, shown.files[index] === null ? [] : [shown.files[index]])}
                </div>
              ))}
              <div className="materials-field">
                <label htmlFor="materials-differs">
                  What is the one thing that differs? (optional)
                </label>
                <input
                  id="materials-differs"
                  data-test-id="materials-differs"
                  readOnly={readOnly}
                  maxLength={MAX_MATERIALS_DIFFERS_CHARS}
                  value={shown.differs}
                  onChange={(event) => setDraft({ ...shown, differs: event.target.value })}
                />
              </div>
            </>
          )}
        </div>

        {!built.ok && (
          <p className="materials-hint" data-test-id="materials-problem">
            {built.problem}
          </p>
        )}

        {uploading && (
          <p className="materials-hint" data-test-id="materials-uploading">
            Save once your files have finished uploading.
          </p>
        )}

        <button
          type="submit"
          className="button-primary"
          data-test-id="materials-save"
          disabled={
            busy || pendingChange !== null || readOnly || uploading || !built.ok || unchanged
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>

      {pendingChange !== null && pendingChange.action !== 'fix' && (
        <ChangeConfirm
          consequences={pendingChange.consequences}
          busy={busy}
          onConfirm={() => {
            if (pendingChange.action === 'save') {
              send(pendingChange.inputs, acknowledgementOf(pendingChange.consequences));
            } else if (pendingChange.action === 'stimulus') {
              choose(
                pendingChange.fileId,
                pendingChange.candidate,
                acknowledgementOf(pendingChange.consequences),
              );
            }
          }}
          onCancel={() => setPendingChange(null)}
        />
      )}

      {saveError !== null && (
        <p className="chat-error" data-test-id="materials-save-error" role="alert">
          {saveError}
        </p>
      )}

      {stimulusError !== null && (
        <p className="chat-error" data-test-id="materials-stimulus-error" role="alert">
          {stimulusError}
        </p>
      )}

      {current !== null && !capturedHere && (
        <p className="materials-notice" data-test-id="materials-type-changed">
          Your study type changed since these materials were saved. Save them again so Sigora reads
          them {typeName === undefined ? 'for it' : `for ${typeName}`}.
        </p>
      )}

      {capturedHere && (
        <section
          className="materials-taken"
          aria-labelledby="materials-taken-heading"
          data-test-id="materials-taken"
        >
          <h2 className="materials-heading" id="materials-taken-heading">
            What Sigora took from your materials
          </h2>

          <div aria-live="polite">
            {waiting && (
              <Pending live={false} className="materials-status" testId="materials-pending">
                Sigora is reading your materials…
              </Pending>
            )}

            {waiting && readingNames.length > 0 && (
              <p className="materials-status" data-test-id="materials-pending-files">
                {`Reading ${nameList(readingNames)}.`}
              </p>
            )}

            {extraction?.status === 'pending' && !waiting && (
              <p className="materials-status" data-test-id="materials-overdue">
                Sigora is taking longer than it should to read your materials.
              </p>
            )}

            {extraction?.status === 'unavailable' && (
              <p className="materials-status" data-test-id="materials-unavailable">
                Sigora could not read your materials just now. Fill in the rows yourself, or ask it
                to read them again.
              </p>
            )}

            {extraction?.status === 'unreadable' && (
              <p className="materials-status" data-test-id="materials-unreadable">
                Sigora could not make sense of what it read. Fill in the rows yourself, or ask it to
                read them again.
              </p>
            )}

            {extraction?.status === 'ready' && extraction.coverage.length > 0 && (
              <ul className="materials-coverage" data-test-id="materials-coverage">
                {extraction.coverage.map((entry) => (
                  <li key={entry.file_id} data-test-id={`materials-coverage-${entry.file_id}`}>
                    <span className="materials-coverage-name">{fileName(entry.file_id)}</span>
                    {`: ${coverageLine(entry)}`}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {settledUnread && (
            <button
              type="button"
              data-test-id="materials-retry"
              disabled={busy || readOnly}
              onClick={() => send(current.content.inputs)}
            >
              Read them again
            </button>
          )}

          <ol className="materials-rows" data-test-id="materials-rows">
            {view.rows.map((row) =>
              editing?.factId === row.factId ? (
                <li
                  className="materials-row"
                  key={row.factId}
                  data-test-id={`materials-row-${row.factId}`}
                >
                  <form className="materials-row-editor" onSubmit={submitFix}>
                    <label htmlFor={`materials-fix-${row.factId}`}>{row.label}</label>
                    <input
                      id={`materials-fix-${row.factId}`}
                      data-test-id="materials-fix-input"
                      ref={editorRef}
                      maxLength={MAX_FACT_VALUE_CHARS}
                      placeholder="Leave empty for none"
                      value={editing.value}
                      onChange={(event) => {
                        setEditing({ ...editing, value: event.target.value });
                        // The confirm was for the value as it was sent.
                        setPendingChange((pending) => (pending?.action === 'fix' ? null : pending));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          closeEditor();
                        }
                      }}
                    />
                    <div className="materials-row-actions">
                      <button
                        type="submit"
                        data-test-id="materials-fix-save"
                        disabled={fixing || pendingChange?.action === 'fix'}
                      >
                        {fixing ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        data-test-id="materials-fix-cancel"
                        disabled={fixing}
                        onClick={closeEditor}
                      >
                        Cancel
                      </button>
                    </div>
                    {pendingChange?.action === 'fix' && (
                      <ChangeConfirm
                        consequences={pendingChange.consequences}
                        busy={busy}
                        onConfirm={() => fix(acknowledgementOf(pendingChange.consequences))}
                        onCancel={() => {
                          setPendingChange(null);
                          editorRef.current?.focus();
                        }}
                      />
                    )}
                    {fixError !== null && (
                      <p className="chat-error" data-test-id="materials-fix-error" role="alert">
                        {fixError}
                      </p>
                    )}
                  </form>
                </li>
              ) : (
                <li
                  className="materials-row"
                  key={row.factId}
                  data-test-id={`materials-row-${row.factId}`}
                >
                  <span className="materials-row-label">{row.label}</span>
                  <span
                    className={
                      row.value === null
                        ? 'materials-row-value materials-row-none'
                        : 'materials-row-value'
                    }
                    data-test-id="materials-row-value"
                  >
                    {rowValue(row)}
                  </span>
                  {row.state === 'changed' && (
                    <span className="materials-row-note" data-test-id="materials-row-changed">
                      changed by your new materials
                    </span>
                  )}
                  {row.source === 'user' && (
                    <span className="materials-row-note" data-test-id="materials-row-fixed">
                      edited by you
                    </span>
                  )}
                  <button
                    type="button"
                    className="materials-row-fix text-link muted"
                    data-test-id="materials-fix"
                    ref={(button) => {
                      if (button === null) {
                        fixButtons.current.delete(row.factId);
                      } else {
                        fixButtons.current.set(row.factId, button);
                      }
                    }}
                    disabled={editing !== null || busy || waiting || readOnly}
                    aria-label={`Edit ${row.label}`}
                    onClick={() => openEditor(row)}
                  >
                    Edit
                  </button>
                </li>
              ),
            )}
          </ol>

          {view.missing.length > 0 && !waiting && (
            <ul className="materials-missing" data-test-id="materials-missing">
              {view.missing.map((fact) => (
                <li key={fact.factId} data-test-id={`materials-missing-${fact.factId}`}>
                  {fact.request}{' '}
                  <button
                    type="button"
                    data-test-id="materials-missing-fix"
                    disabled={editing !== null || busy || readOnly}
                    onClick={() => {
                      const row = view.rows.find((candidate) => candidate.factId === fact.factId);
                      if (row !== undefined) {
                        openEditor(row);
                      }
                    }}
                  >
                    Fix the row
                  </button>{' '}
                  <button
                    type="button"
                    data-test-id="materials-missing-add"
                    disabled={readOnly}
                    onClick={focusIntake}
                  >
                    Add it to your materials
                  </button>
                </li>
              ))}
            </ul>
          )}

          {confirmError !== null && (
            <p
              ref={refusal}
              className="chat-error"
              data-test-id="materials-confirm-error"
              role="alert"
              tabIndex={-1}
            >
              {confirmError}
            </p>
          )}

          {view.confirmedCurrent && (
            <p className="materials-confirmed" data-test-id="materials-confirmed">
              Confirmed. Sigora works from these facts.
            </p>
          )}
        </section>
      )}

      {view.current !== null && (
        <>
          {/*
            Every version the Materials have had, newest first, with the files each named. A file
            opens through the study's own file download; no earlier version's facts are shown.
          */}
          <button
            type="button"
            className="text-link muted"
            aria-expanded={historyOpen}
            aria-controls="materials-history"
            data-test-id="materials-history-toggle"
            onClick={() => setHistoryOpen((open) => !open)}
          >
            {historyOpen ? MATERIALS_HISTORY_TOGGLE.open : MATERIALS_HISTORY_TOGGLE.closed}
          </button>
          {historyOpen && history === null && historyError === null && (
            <p className="materials-hint" data-test-id="materials-history-loading">
              Reading the history…
            </p>
          )}
          {historyOpen && historyError !== null && (
            <p className="chat-error" data-test-id="materials-history-error" role="alert">
              {historyError}
            </p>
          )}
          {historyOpen && history !== null && (
            <ol
              className="materials-history"
              id="materials-history"
              data-test-id="materials-history"
            >
              {history.map((entry) => (
                <li key={entry.version} data-test-id={`materials-history-${String(entry.version)}`}>
                  {materialsHistoryLine(entry)}.{' '}
                  <span className="artifact-meta">{dateOf(entry.createdAt)}</span>
                  {entry.files.length > 0 && (
                    <ul className="materials-history-files">
                      {entry.files.map((file) => (
                        <li key={file.id}>
                          <a
                            href={materialsFileUrl(studyId, file.id)}
                            download={file.name}
                            data-test-id="materials-history-file"
                          >
                            {file.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
};
